import { useState } from "react";
import { Archive, LogOut, Plus, Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { stripMarkdownToText } from "~/lib/stripMarkdownToText";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";
import { SettingsRow } from "~/pages/manifold-nextgen/components/settings/SettingsRow";
import { SettingsSection } from "~/pages/manifold-nextgen/components/settings/SettingsSection";
import { buildExpandedAccess } from "~/pages/manifold-nextgen/components/share/AccessDetailsView";
import { useProjectEntityRemoval } from "~/pages/manifold-nextgen/hooks/useProjectEntityRemoval";
import { useProjectExit } from "~/pages/manifold-nextgen/hooks/useProjectExit";
import { useProjectShare } from "~/pages/manifold-nextgen/hooks/useProjectShare";
import { ROUTE_NOVA_VIEW, nextGenDatasetDetailPath } from "~/router/constants";
import { AgentStatus } from "~/services/agents";
import { DeleteProjectDialog } from "./DeleteProjectDialog";
import { FundingSourceSection } from "./FundingSourceSection";
import { LeaveProjectDialog } from "./LeaveProjectDialog";
import { ProjectAgentsDialog } from "./ProjectAgentsDialog";
import { ProjectDatasetsDialog } from "./ProjectDatasetsDialog";
import { useProjectContext } from "./useProjectContext";

// Stands in for the attached rows until they arrive, so "none attached" is only
// ever said about a list that has actually loaded.
const AttachedRowsSkeleton = () => (
  <div className="divide-y divide-border" aria-busy="true">
    {[0, 1].map((row) => (
      <div key={row} className="flex items-center gap-3 px-4 py-3">
        <Skeleton className="size-5 shrink-0 rounded-md" />
        <Skeleton className="h-3.5 w-44" />
      </div>
    ))}
  </div>
);

export const ProjectSettings = () => {
  const { projectId, project, isOwner, attachedDatasets, attachedAgents } = useProjectContext();
  const { handleArchive, handleLeave } = useProjectExit(projectId, project.name);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [addDatasetsOpen, setAddDatasetsOpen] = useState(false);
  const [addAgentsOpen, setAddAgentsOpen] = useState(false);

  const { collaborators, accessLevels } = useProjectShare(projectId);
  const memberCount = collaborators
    ? buildExpandedAccess(collaborators.users, collaborators.groups, accessLevels).length
    : undefined;

  const { handleRemoveDataset, handleRemoveAgent, isRemovingDataset, isRemovingAgent } =
    useProjectEntityRemoval(projectId, attachedDatasets, attachedAgents);

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mx-auto flex max-w-3xl flex-col gap-6">
        <SettingsSection
          title="Datasets"
          headerAction={
            isOwner && (
              <Button
                variant="ghost"
                size="sm"
                className="text-primary hover:text-primary/80"
                onClick={() => setAddDatasetsOpen(true)}
              >
                <Plus className="size-4" />
                Add Datasets
              </Button>
            )
          }
        >
          {attachedDatasets === undefined ? (
            <AttachedRowsSkeleton />
          ) : attachedDatasets.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm italic text-muted-foreground">
              No datasets attached.
            </p>
          ) : (
            attachedDatasets.map((dataset) => (
              <SettingsRow
                key={dataset.id}
                icon={<DatasetIcon dataset={dataset} size={20} />}
                title={dataset.title}
                description={dataset.description}
                href={nextGenDatasetDetailPath(dataset.id)}
              >
                {isOwner && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
                    onClick={() => handleRemoveDataset(dataset.id)}
                    disabled={isRemovingDataset}
                    aria-label={`Remove ${dataset.title}`}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </SettingsRow>
            ))
          )}
        </SettingsSection>

        <SettingsSection
          title="Agents"
          headerAction={
            isOwner && (
              <Button
                variant="ghost"
                size="sm"
                className="text-primary hover:text-primary/80"
                onClick={() => setAddAgentsOpen(true)}
              >
                <Plus className="size-4" />
                Add Agents
              </Button>
            )
          }
        >
          {attachedAgents === undefined ? (
            <AttachedRowsSkeleton />
          ) : attachedAgents.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm italic text-muted-foreground">
              No agents attached.
            </p>
          ) : (
            attachedAgents.map((agent) => (
              <SettingsRow
                key={agent.id}
                icon={<AgentAvatar agent={agent} size={32} />}
                title={agent.display_name}
                description={agent.description && stripMarkdownToText(agent.description)}
                href={
                  agent.status !== AgentStatus.DELETED
                    ? `${ROUTE_NOVA_VIEW}/agents/${agent.id}`
                    : undefined
                }
              >
                {isOwner && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
                    onClick={() => handleRemoveAgent(agent.id)}
                    disabled={isRemovingAgent}
                    aria-label={`Remove ${agent.display_name}`}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </SettingsRow>
            ))
          )}
        </SettingsSection>

        <FundingSourceSection workbenchId={project.workbench_id} />

        <SettingsSection title="Danger Zone" danger>
          <SettingsRow
            title="Leave project"
            description="You'll lose access to this project unless it's shared with you again."
          >
            <Button variant="outline" className="w-32" onClick={() => setLeaveOpen(true)}>
              <LogOut className="size-4" />
              Leave
            </Button>
          </SettingsRow>
          {isOwner && (
            <SettingsRow
              title="Archive project"
              description="Archive and hide this project. An admin can restore if necessary."
            >
              <Button variant="destructive" className="w-32" onClick={() => setDeleteOpen(true)}>
                <Archive className="size-4" />
                Archive
              </Button>
            </SettingsRow>
          )}
        </SettingsSection>
      </div>

      <DeleteProjectDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        projectName={project.name}
        memberCount={memberCount}
        onConfirm={handleArchive}
      />
      <LeaveProjectDialog
        open={leaveOpen}
        onOpenChange={setLeaveOpen}
        projectName={project.name}
        onConfirm={handleLeave}
      />
      <ProjectDatasetsDialog
        projectId={projectId}
        open={addDatasetsOpen}
        onOpenChange={setAddDatasetsOpen}
        attachedIds={(attachedDatasets ?? []).map((d) => d.id)}
      />
      <ProjectAgentsDialog
        projectId={projectId}
        open={addAgentsOpen}
        onOpenChange={setAddAgentsOpen}
        attached={attachedAgents ?? []}
      />
    </div>
  );
};
